import React from 'react';
import { PrintPreviewChrome } from '@/components/print/PrintPreviewChrome';
import { formatMoney, PRINT_PLAIN_AMOUNT_PROFILE } from '@/utils/format/money';
import { formatAmountInWordsPlain } from '@/utils/amountInWords';
import type { PayrollLabourCard } from '@/services/hrms.service';
import './PayrollLabourCardSheet.css';

const amt = (n: number) => formatMoney(n, PRINT_PLAIN_AMOUNT_PROFILE);

const STATUS_ROWS: Array<{ label: string; statuses: string[] }> = [
  { label: 'Present', statuses: ['Present', 'HalfDay'] },
  { label: 'Absent', statuses: ['Absent', 'HalfDay'] },
  { label: 'Leave', statuses: ['Leave'] },
  { label: 'Holiday', statuses: ['Holiday'] },
  { label: 'Week Off', statuses: ['WeeklyOff'] },
];

function Check({ on, half }: { on: boolean; half?: boolean }) {
  if (!on) return <span className="lc-box" />;
  return <span className="lc-box lc-box--on">{half ? '½' : '✓'}</span>;
}

function DayGrid({ days }: { days: PayrollLabourCard['days'] }) {
  return (
    <table className="lc-grid">
      <colgroup>
        <col className="lc-grid-label" />
        {days.map((d) => (
          <col key={d.date} />
        ))}
      </colgroup>
      <tbody>
        <tr>
          <th>Day</th>
          {days.map((d) => (
            <td key={d.date}>{d.weekday}</td>
          ))}
        </tr>
        <tr>
          <th>Date</th>
          {days.map((d) => (
            <td key={d.date} className="lc-strong">
              {Number(d.date.slice(8, 10))}
            </td>
          ))}
        </tr>
        <tr className="lc-grid-project">
          <th>Project Code</th>
          {days.map((d) => (
            <td key={d.date}>
              <span className="lc-vertical">{d.project_codes.join(', ')}</span>
            </td>
          ))}
        </tr>
        {STATUS_ROWS.map((row) => (
          <tr key={row.label}>
            <th>{row.label}</th>
            {days.map((d) => (
              <td key={d.date}>
                <Check on={row.statuses.includes(d.status || '')} half={d.status === 'HalfDay'} />
              </td>
            ))}
          </tr>
        ))}
        <tr>
          <th>Overtime</th>
          {days.map((d) => (
            <td key={d.date}>{d.ot_hours > 0 ? d.ot_hours : ''}</td>
          ))}
        </tr>
      </tbody>
    </table>
  );
}

function LabourCardPage({ card }: { card: PayrollLabourCard }) {
  const firstHalf = card.days.slice(0, 15);
  const secondHalf = card.days.slice(15);
  const otTotal = card.ot.reduce((s, o) => s + o.amount, 0);
  return (
    <div className="print-doc-a4 lc-page">
      <div className="lc-sheet">
        <div className="lc-head">
          <div className="lc-company">{card.company_name || ''}</div>
          <div className="lc-title">LABOUR CARD — {card.period_label}</div>
          <div className="lc-run">Payroll run {card.run_number}</div>
        </div>

        <table className="lc-info">
          <tbody>
            <tr>
              <th>File No</th>
              <td>{card.employee.file_no}</td>
              <th>Employee Name</th>
              <td>{card.employee.name}</td>
            </tr>
            <tr>
              <th>Employee Job</th>
              <td>{card.employee.job_title || '—'}</td>
              <th>Department</th>
              <td>{card.employee.department_name || '—'}</td>
            </tr>
          </tbody>
        </table>

        <div className="lc-legend">P = Present · A = Absent · L = Leave · H = Holiday · W = Week Off · ½ = Half day</div>

        <DayGrid days={firstHalf} />
        {secondHalf.length > 0 && <DayGrid days={secondHalf} />}

        <div className="lc-bottom">
          <table className="lc-box-table">
            <thead>
              <tr>
                <th colSpan={2}>Salary ({card.currency})</th>
              </tr>
            </thead>
            <tbody>
              {card.salary.map((s) => (
                <tr key={s.code}>
                  <td>{s.name}</td>
                  <td className="lc-num">{amt(s.monthly)}</td>
                </tr>
              ))}
              <tr className="lc-total">
                <td>Monthly Salary</td>
                <td className="lc-num">{amt(card.monthly_salary)}</td>
              </tr>
            </tbody>
          </table>

          <div className="lc-calc">
            <table className="lc-box-table">
              <thead>
                <tr>
                  <th>Working Days</th>
                  <th>W/Day</th>
                  <th>Working Amt</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="lc-num">{card.working_days}</td>
                  <td className="lc-num">{amt(card.daily_rate)}</td>
                  <td className="lc-num lc-strong">{amt(card.working_amount)}</td>
                </tr>
              </tbody>
            </table>

            <table className="lc-box-table">
              <thead>
                <tr>
                  <th>Overtime</th>
                  <th>Hours</th>
                  <th>Rate</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {card.ot.map((o) => (
                  <tr key={o.kind}>
                    <td>{o.label}</td>
                    <td className="lc-num">{o.hours}</td>
                    <td className="lc-num">{amt(o.rate)}</td>
                    <td className="lc-num">{amt(o.amount)}</td>
                  </tr>
                ))}
                <tr className="lc-total">
                  <td colSpan={3}>Total O.T.</td>
                  <td className="lc-num">{amt(otTotal)}</td>
                </tr>
              </tbody>
            </table>

            <table className="lc-box-table">
              <tbody>
                <tr>
                  <td>SP. Additionals</td>
                  <td className="lc-num">{amt(card.total_additions)}</td>
                </tr>
                {card.additions.map((a, i) => (
                  <tr key={`a-${i}`} className="lc-sub">
                    <td>{a.name}</td>
                    <td className="lc-num">{amt(a.amount)}</td>
                  </tr>
                ))}
                <tr>
                  <td>Deductions</td>
                  <td className="lc-num">{card.total_deductions ? `-${amt(card.total_deductions)}` : amt(0)}</td>
                </tr>
                {card.deductions.map((d, i) => (
                  <tr key={`d-${i}`} className="lc-sub">
                    <td>{d.name}</td>
                    <td className="lc-num">-{amt(d.amount)}</td>
                  </tr>
                ))}
                <tr className="lc-net">
                  <td>Net Salary ({card.currency})</td>
                  <td className="lc-num">{amt(card.net)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="lc-signs">
          <div>Prepared by</div>
          <div>Checked by</div>
          <div>Approved by</div>
        </div>

        {card.show_cash_voucher ? (
          <div className="lc-voucher">
            <div className="lc-voucher-title">CASH PAYMENT VOUCHER</div>
            <div className="lc-voucher-row">
              <span>Date: ____________________</span>
              <span className="lc-strong">
                {card.currency} {amt(card.net)}
              </span>
            </div>
            <div className="lc-voucher-row">
              <span>
                Paid to: <b>{card.employee.name}</b> ({card.employee.file_no})
              </span>
            </div>
            <div className="lc-voucher-row">
              <span>
                The sum of {card.currency}: <b>{formatAmountInWordsPlain(card.net)}</b>
              </span>
            </div>
            <div className="lc-voucher-row">
              <span>Being: Salary for {card.period_label}</span>
            </div>
            <div className="lc-signs lc-signs--voucher">
              <div>Approved by</div>
              <div>Received by</div>
            </div>
          </div>
        ) : (
          <table className="lc-info lc-bank">
            <tbody>
              <tr>
                <th>Payment</th>
                <td>{card.employee.payment_method || 'Not set'}</td>
                <th>Bank</th>
                <td>{card.employee.bank_name || '—'}</td>
                <th>IBAN</th>
                <td>{card.employee.iban || '—'}</td>
              </tr>
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export const PayrollLabourCardSheet: React.FC<{
  cards: PayrollLabourCard[];
  onClose: () => void;
}> = ({ cards, onClose }) => {
  const first = cards[0];
  const fileName =
    cards.length === 1 && first
      ? `labour-card-${first.employee.file_no}-${first.run_number}`
      : `labour-cards-${first?.run_number || ''}`;
  return (
    <PrintPreviewChrome
      title={cards.length === 1 ? `Labour card — ${first?.employee.name}` : `Labour cards (${cards.length})`}
      fileName={fileName}
      onBack={onClose}
    >
      {cards.map((c) => (
        <LabourCardPage key={c.employee.id} card={c} />
      ))}
    </PrintPreviewChrome>
  );
};

export default PayrollLabourCardSheet;
